import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, imageUrl, categoryLabel } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ImagePlus, Type, Sparkles, ShoppingBag, Check, Move, Trash2, ChevronUp, ChevronDown, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { downloadDesign } from "../lib/designRender";

const SIZE_SURCHARGE = { "2X": 3, "3X": 3, "4X": 3 };
const TEXT_COLORS = ["#F4F1EA", "#1B2A41", "#9A2A2B", "#C59B6D"];
const MAX_IMAGES = 8;

const makeId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// Downscale an uploaded image to keep the checkout payload manageable
const downscaleImage = (file, maxDim = 1000) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function ProductStudio() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);

  const [color, setColor] = useState(null);
  const [quantity, setQuantity] = useState(1); // used only when product has no sizes
  const [sizeQty, setSizeQty] = useState({}); // { size: qty } for products with sizes
  const [itemNote, setItemNote] = useState("");
  const [activeView, setActiveView] = useState("live"); // "live" | gallery image id

  const [layers, setLayers] = useState([]); // {id,type,src,name,preset_id,text,color,box}
  const [selectedId, setSelectedId] = useState(null);
  const [showPresets, setShowPresets] = useState(false);
  const [library, setLibrary] = useState([]); // [{id,name,image_url,category}]
  const [presetTab, setPresetTab] = useState("own");
  const [presetSearch, setPresetSearch] = useState("");

  const previewRef = useRef(null);
  const dragState = useRef(null); // { mode, id, startX, startY, box }

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      const p = r.data;
      setProduct(p);
      setColor(p.colors?.[0]);
      setSizeQty({});
      setLayers([]);
      setSelectedId(null);
    });
  }, [id]);

  useEffect(() => {
    api.get("/presets/library").then((r) => setLibrary(r.data.presets || [])).catch(() => {});
  }, []);

  const getDesignArea = () => {
    if (!product) return { x: 30, y: 30, w: 40, h: 40 };
    return product.category === "tshirt"
      ? product.mockup_overlay_box || { x: 30, y: 30, w: 40, h: 40 }
      : { x: 17, y: 22, w: 66, h: 55 }; // ~10"w x 8.3"h printable area
  };

  const newImageBox = (n = 0) => {
    const a = getDesignArea();
    const w = a.w * 0.5, h = a.h * 0.5, off = (n % 4) * 4;
    return {
      w, h,
      x: Math.max(0, Math.min(100 - w, a.x + a.w * 0.25 + off)),
      y: Math.max(0, Math.min(100 - h, a.y + a.h * 0.25 + off)),
    };
  };
  const newTextBox = (n = 0) => {
    const a = getDesignArea();
    const w = a.w * 0.85, h = a.h * 0.22, off = (n % 4) * 4;
    return {
      w, h,
      x: a.x + (a.w - w) / 2,
      y: Math.max(0, Math.min(100 - h, a.y + a.h * 0.4 + off)),
    };
  };

  const addPreset = (d) => {
    const existing = layers.find((l) => l.type === "preset");
    const lid = existing?.id || makeId();
    const layer = {
      id: lid, type: "preset", src: d.image_url, name: d.name, preset_id: d.id,
      box: existing?.box || { ...getDesignArea() },
    };
    setLayers((ls) => [layer, ...ls.filter((l) => l.type !== "preset")]);
    setSelectedId(lid);
    setShowPresets(false);
  };

  const addImageLayer = (dataUrl) => {
    const imgs = layers.filter((l) => l.type === "image");
    const lid = makeId();
    setLayers((ls) => [...ls, { id: lid, type: "image", src: dataUrl, box: newImageBox(imgs.length) }]);
    setSelectedId(lid);
  };

  const addTextLayer = () => {
    const texts = layers.filter((l) => l.type === "text");
    const lid = makeId();
    setLayers((ls) => [...ls, { id: lid, type: "text", text: "Your text", color: "#F4F1EA", box: newTextBox(texts.length) }]);
    setSelectedId(lid);
  };

  const onUploadLayer = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Image too large. Max 15MB."); e.target.value = ""; return; }
    if (layers.filter((l) => l.type === "image").length >= MAX_IMAGES) {
      toast.error(`Up to ${MAX_IMAGES} photos per design.`); e.target.value = ""; return;
    }
    try {
      const dataUrl = await downscaleImage(f);
      addImageLayer(dataUrl);
    } catch {
      toast.error("Could not load that image");
    }
    e.target.value = "";
  };

  const updateLayer = (lid, patch) => setLayers((ls) => ls.map((l) => (l.id === lid ? { ...l, ...patch } : l)));
  const deleteLayer = (lid) => {
    setLayers((ls) => ls.filter((l) => l.id !== lid));
    setSelectedId((s) => (s === lid ? null : s));
  };
  const moveLayer = (lid, dir) => {
    setLayers((ls) => {
      const i = ls.findIndex((l) => l.id === lid);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ls.length) return ls;
      const copy = [...ls];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const startMove = (e, layer) => {
    e.stopPropagation();
    setSelectedId(layer.id);
    dragState.current = { mode: "move", id: layer.id, startX: e.clientX, startY: e.clientY, box: { ...layer.box } };
  };
  const startResize = (e, layer) => {
    e.stopPropagation();
    e.preventDefault();
    dragState.current = { mode: "resize", id: layer.id, startX: e.clientX, startY: e.clientY, box: { ...layer.box } };
  };
  const onPreviewPointerMove = (e) => {
    const st = dragState.current;
    if (!st || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const dx = ((e.clientX - st.startX) / rect.width) * 100;
    const dy = ((e.clientY - st.startY) / rect.height) * 100;
    setLayers((ls) =>
      ls.map((l) => {
        if (l.id !== st.id) return l;
        if (st.mode === "move") {
          const x = Math.max(0, Math.min(100 - st.box.w, st.box.x + dx));
          const y = Math.max(0, Math.min(100 - st.box.h, st.box.y + dy));
          return { ...l, box: { ...l.box, x, y } };
        }
        const ratio = st.box.h / st.box.w;
        let w = Math.max(8, Math.min(100 - st.box.x, st.box.w + dx));
        let h = w * ratio;
        if (h > 100 - st.box.y) { h = 100 - st.box.y; w = h / ratio; }
        return { ...l, box: { ...l.box, w, h } };
      })
    );
  };
  const endDrag = () => { dragState.current = null; };

  const saveDesign = async () => {
    if (layers.length === 0) { toast.error("Add a design first."); return; }
    const url = await downloadDesign(
      layers.map((l) => ({ type: l.type, src: l.src, text: l.text, color: l.color, box: l.box })),
      `${(product?.name || "papag-design").toLowerCase().replace(/\s+/g, "-")}.png`
    );
    if (url) toast.success("Design saved (PNG)");
    else toast.error("Couldn't save the design image");
  };

  if (!product) return <div className="max-w-[1400px] mx-auto p-12 text-[var(--ink-soft)]">Loading…</div>;

  const isCustomizable = product.customizable !== false;
  const sizedMode = (product.sizes?.length || 0) > 0;
  const priceForSize = (s) => product.base_price + (SIZE_SURCHARGE[s] || 0);
  const totalQty = sizedMode
    ? (product.sizes || []).reduce((a, s) => a + (sizeQty[s] || 0), 0)
    : quantity;
  const addTotal = sizedMode
    ? (product.sizes || []).reduce((sum, s) => sum + (sizeQty[s] || 0) * priceForSize(s), 0)
    : product.base_price * quantity;
  const setQtyForSize = (s, v) => setSizeQty((prev) => ({ ...prev, [s]: Math.max(0, v) }));
  const gallery = product.gallery_images || [];
  const activeGalleryImg = gallery.find((g) => g.id === activeView);
  const designArea = getDesignArea();
  const imageCount = layers.filter((l) => l.type === "image").length;

  const handleAddToCart = () => {
    if ((product.colors?.length || 0) > 0 && !color) { toast.error("Pick a color first."); return; }
    if (sizedMode && totalQty === 0) { toast.error("Choose a quantity for at least one size."); return; }
    if (isCustomizable && layers.length === 0) {
      toast.error("Add a design — a preset, a photo, or some text (you can stack several).");
      return;
    }
    const emptyText = layers.find((l) => l.type === "text" && !(l.text || "").trim());
    if (emptyText) { toast.error("One of your text layers is empty — type something or delete it."); return; }

    const presetL = layers.find((l) => l.type === "preset");
    const textLs = layers.filter((l) => l.type === "text");
    const imageLs = layers.filter((l) => l.type === "image");
    const parts = [presetL && "preset", imageLs.length && "custom", textLs.length && "text"].filter(Boolean);

    const baseCustomization = {
      color_name: color?.name || null,
      color_hex: color?.hex || null,
      design_type: isCustomizable ? (parts.join("+") || "none") : "none",
      preset_id: presetL?.preset_id || null,
      preset_name: presetL?.name || null,
      preset_image: presetL?.src || null,
      custom_text: textLs.map((t) => t.text).join(" / ") || null,
      custom_image_data: imageLs[0]?.src || null,
      text_color: textLs[0]?.color || null,
      design_transform: presetL?.box || imageLs[0]?.box || textLs[0]?.box || null,
      layers: isCustomizable
        ? layers.map((l) => ({
            id: l.id, type: l.type, src: l.src || null, name: l.name || null,
            preset_id: l.preset_id || null, text: l.text || null, color: l.color || null, box: l.box,
          }))
        : null,
      note: itemNote.trim() || null,
    };

    const base = {
      product_id: product.id,
      product_name: product.name,
      mockup_image: product.mockup_image,
      shipping_fee: product.shipping_fee != null ? product.shipping_fee : null,
      exclude_from_discounts: !!product.exclude_from_discounts,
      exclude_from_free_shipping: !!product.exclude_from_free_shipping,
    };

    if (sizedMode) {
      (product.sizes || []).forEach((s) => {
        const q = sizeQty[s] || 0;
        if (q > 0) {
          addItem({ ...base, unit_price: priceForSize(s), quantity: q, customization: { ...baseCustomization, size: s } });
        }
      });
    } else {
      addItem({ ...base, unit_price: product.base_price, quantity, customization: { ...baseCustomization, size: null } });
    }
    toast.success("Added to cart");
    nav("/cart");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="product-studio">
      <div className="grid lg:grid-cols-12 gap-12">
        {/* PREVIEW */}
        <div className="lg:col-span-7 lg:sticky lg:top-28 lg:self-start">
          <div
            ref={previewRef}
            onPointerMove={onPreviewPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            onPointerDown={() => setSelectedId(null)}
            className="relative aspect-square rounded-sm overflow-hidden bg-[var(--bone-2)] border border-[rgba(27,42,65,0.1)] select-none"
            style={{ backgroundColor: color?.hex ? `${color.hex}20` : undefined }}
            data-testid="studio-preview"
          >
            {activeGalleryImg ? (
              <img src={imageUrl(activeGalleryImg.image_url)} alt="" className="absolute inset-0 w-full h-full object-contain p-2" data-testid="preview-gallery-image" />
            ) : (
              <>
                <img src={imageUrl(product.mockup_image)} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                {color?.hex && (
                  <div className="absolute inset-0 mix-blend-multiply pointer-events-none" style={{ backgroundColor: color.hex, opacity: 0.55 }} />
                )}
                {isCustomizable && (
                  <div
                    className="absolute border-2 border-dashed border-[var(--gold)]/40 pointer-events-none rounded-sm"
                    style={{ left: `${designArea.x}%`, top: `${designArea.y}%`, width: `${designArea.w}%`, height: `${designArea.h}%` }}
                    data-testid="design-area-guide"
                  />
                )}
                {layers.map((layer) => {
                  const sel = layer.id === selectedId;
                  return (
                    <div
                      key={layer.id}
                      onPointerDown={(e) => startMove(e, layer)}
                      className={`absolute touch-none transition-[outline] ${sel ? "outline outline-2 outline-[var(--gold)] z-10" : "hover:outline hover:outline-1 hover:outline-[var(--gold)]/60"}`}
                      style={{ left: `${layer.box.x}%`, top: `${layer.box.y}%`, width: `${layer.box.w}%`, height: `${layer.box.h}%`, touchAction: "none" }}
                      data-testid={`layer-${layer.id}`}
                    >
                      {layer.type === "text" ? (
                        <div className="w-full h-full grid place-items-center text-center break-words font-serif-display leading-none pointer-events-none" style={{ containerType: "size", color: layer.color, textShadow: "0 2px 8px rgba(0,0,0,0.35)" }}>
                          <span style={{ fontSize: "42cqh" }}>{layer.text || " "}</span>
                        </div>
                      ) : (
                        <img src={imageUrl(layer.src)} alt="" className="w-full h-full object-contain pointer-events-none drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)]" />
                      )}
                      {sel && (
                        <>
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[var(--navy)] text-[var(--bone)] rounded-full p-1 pointer-events-none shadow">
                            <Move className="w-3 h-3" />
                          </div>
                          <div
                            onPointerDown={(e) => startResize(e, layer)}
                            className="absolute -bottom-2 -right-2 w-5 h-5 rounded-full bg-[var(--gold)] border-2 border-white cursor-nwse-resize touch-none shadow"
                            style={{ touchAction: "none" }}
                            data-testid={`layer-resize-${layer.id}`}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* THUMBNAILS */}
          {gallery.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="studio-thumbnails">
              <button type="button" onClick={() => setActiveView("live")} data-testid="studio-thumb-live"
                className={`relative w-16 h-16 rounded-sm overflow-hidden border-2 transition bg-[var(--bone-2)] ${activeView === "live" ? "border-[var(--gold)]" : "border-[rgba(27,42,65,0.12)] hover:border-[var(--navy)]"}`}>
                <img src={imageUrl(product.mockup_image)} alt="Live preview" className="w-full h-full object-cover" />
                <span className="absolute inset-x-0 bottom-0 bg-[var(--navy)]/80 text-[var(--bone)] font-mono-ui text-[7px] tracking-[0.15em] text-center py-0.5">LIVE</span>
              </button>
              {gallery.map((g) => (
                <button type="button" key={g.id} onClick={() => setActiveView(g.id)} data-testid={`studio-thumb-${g.id}`}
                  className={`w-16 h-16 rounded-sm overflow-hidden border-2 transition bg-[var(--bone-2)] ${activeView === g.id ? "border-[var(--gold)]" : "border-[rgba(27,42,65,0.12)] hover:border-[var(--navy)]"}`}>
                  <img src={imageUrl(g.image_url)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between font-mono-ui text-[10px] tracking-[0.25em] text-[var(--ink-soft)]">
            <span>{activeGalleryImg ? "PRODUCT PHOTO" : `LIVE PREVIEW · MOCKUP ${color?.name?.toUpperCase() || ""}`}</span>
            <span>{sizedMode ? "FROM " : ""}$ {product.base_price.toFixed(2)} EACH</span>
          </div>
          {activeView === "live" && selectedId && (
            <div className="mt-1 text-[11px] text-[var(--ink-soft)]" data-testid="studio-placement-hint">
              Drag the selected layer to move · drag the gold corner to resize.
            </div>
          )}
        </div>

        {/* CONFIG */}
        <div className="lg:col-span-5 space-y-8">
          <div>
            <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">{categoryLabel(product.category)}</div>
            <h1 className="font-serif-display text-5xl text-[var(--navy)] mt-2 leading-tight">{product.name}</h1>
            <div className="mt-4 font-serif-display text-3xl text-[var(--crimson)]">{sizedMode ? "from " : ""}${product.base_price.toFixed(2)}</div>
            {(product.exclude_from_discounts || product.exclude_from_free_shipping) && (
              <div className="flex flex-wrap gap-2 mt-3">
                {product.exclude_from_discounts && (
                  <span className="inline-block bg-[var(--crimson)]/10 text-[var(--crimson)] px-2.5 py-1 rounded-sm font-mono-ui text-[9px] tracking-[0.2em] uppercase" data-testid="studio-no-discount-badge">No Discounts</span>
                )}
                {product.exclude_from_free_shipping && (
                  <span className="inline-block bg-[var(--navy)]/10 text-[var(--navy)] px-2.5 py-1 rounded-sm font-mono-ui text-[9px] tracking-[0.2em] uppercase" data-testid="studio-no-freeship-badge">Shipping Always Applies</span>
                )}
              </div>
            )}
            <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">{product.description}</p>
          </div>

          {/* COLOR */}
          {(product.colors?.length || 0) > 0 && (
            <div>
              <div className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[var(--navy)] mb-3">Color · <span className="text-[var(--ink-soft)]">{color?.name}</span></div>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((c) => (
                  <button key={c.name} onClick={() => setColor(c)} data-testid={`config-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`relative w-12 h-12 rounded-full border-2 transition ${color?.name === c.name ? "border-[var(--gold)] scale-110" : "border-[rgba(27,42,65,0.15)]"}`}
                    style={{ backgroundColor: c.hex }} aria-label={c.name}>
                    {color?.name === c.name && <Check className="absolute inset-0 m-auto w-5 h-5 text-[var(--bone)] mix-blend-difference" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* SIZE + PER-SIZE QUANTITY */}
          {sizedMode && (
            <div>
              <div className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[var(--navy)] mb-1">Sizes & Quantities</div>
              <div className="text-[11px] text-[var(--ink-soft)] mb-3">Set a quantity for each size you want — they'll be added as separate lines with the same design.</div>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((s) => {
                  const extra = SIZE_SURCHARGE[s] || 0;
                  const q = sizeQty[s] || 0;
                  return (
                    <div key={s} data-testid={`size-block-${s.toLowerCase()}`}
                      className={`rounded-sm border px-3 py-2.5 flex flex-col items-center gap-2 transition ${q > 0 ? "border-[var(--navy)] bg-[var(--navy)]/5" : "border-[rgba(27,42,65,0.2)]"}`}>
                      <div className="font-mono-ui text-xs tracking-[0.15em] text-[var(--navy)] leading-tight text-center">
                        {s}
                        {extra > 0 && <span className="block text-[9px] text-[var(--crimson)]">+${extra}</span>}
                      </div>
                      <div className="flex items-center border border-[rgba(27,42,65,0.2)] rounded-sm bg-[var(--bone)]">
                        <button type="button" onClick={() => setQtyForSize(s, q - 1)} data-testid={`size-qty-minus-${s.toLowerCase()}`} className="px-2.5 py-1.5 text-[var(--navy)] disabled:opacity-30" disabled={q === 0}>−</button>
                        <input
                          type="number" min="0" value={q}
                          onChange={(e) => setQtyForSize(s, parseInt(e.target.value || "0", 10))}
                          data-testid={`size-qty-input-${s.toLowerCase()}`}
                          className="w-10 text-center font-mono-ui text-sm bg-transparent border-0 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button type="button" onClick={() => setQtyForSize(s, q + 1)} data-testid={`size-qty-plus-${s.toLowerCase()}`} className="px-2.5 py-1.5 text-[var(--navy)]">+</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalQty > 0 && (
                <div className="mt-3 font-mono-ui text-[11px] tracking-[0.15em] text-[var(--ink-soft)]" data-testid="size-total-qty">
                  {totalQty} item{totalQty !== 1 ? "s" : ""} selected
                </div>
              )}
            </div>
          )}

          {/* DESIGN — LAYERS */}
          {isCustomizable && (
            <div>
              <div className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[var(--navy)] mb-1">Design</div>
              <p className="text-xs text-[var(--ink-soft)] mb-3">Build it in layers — presets sit on the bottom, photos and text stack on top. Click a layer to move &amp; resize it.</p>

              <div className="grid grid-cols-3 gap-2">
                {((product.preset_designs?.length || 0) > 0 || library.length > 0) && (
                  <button onClick={() => setShowPresets((v) => !v)} data-testid="add-preset-btn" aria-pressed={showPresets}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-sm border transition ${showPresets ? "bg-[var(--navy)] text-[var(--bone)] border-[var(--navy)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)] hover:border-[var(--navy)]"}`}>
                    <Sparkles className="w-4 h-4" />
                    <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase">Designs</span>
                  </button>
                )}
                <label data-testid="add-image-btn"
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-sm border transition cursor-pointer ${imageCount >= MAX_IMAGES ? "opacity-40 cursor-not-allowed border-[rgba(27,42,65,0.2)] text-[var(--navy)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)] hover:border-[var(--navy)]"}`}>
                  <ImagePlus className="w-4 h-4" />
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase">Photo</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onUploadLayer} disabled={imageCount >= MAX_IMAGES} data-testid="add-image-input" />
                </label>
                <button onClick={addTextLayer} data-testid="add-text-btn"
                  className="flex flex-col items-center gap-1.5 py-3 rounded-sm border border-[rgba(27,42,65,0.2)] text-[var(--navy)] hover:border-[var(--navy)] transition">
                  <Type className="w-4 h-4" />
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase">Text</span>
                </button>
              </div>
              {imageCount >= MAX_IMAGES && (
                <div className="mt-1.5 text-[11px] text-[var(--crimson)]">Photo limit reached ({MAX_IMAGES}).</div>
              )}

              {showPresets && (() => {
                const ownPresets = product.preset_designs || [];
                const otherCats = library
                  .map((d) => d.category)
                  .filter((c, i, arr) => arr.indexOf(c) === i);
                const tabs = [
                  ...(ownPresets.length ? [{ key: "own", label: "This Product" }] : []),
                  ...otherCats.map((c) => ({ key: c, label: categoryLabel(c) })),
                  ...(library.length ? [{ key: "all", label: "All" }] : []),
                ];
                const keys = tabs.map((t) => t.key);
                const active = keys.includes(presetTab) ? presetTab : (keys[0] || "own");
                const base = active === "own" ? ownPresets
                  : active === "all"
                    ? library.filter((d, i, arr) => arr.findIndex((x) => x.image_url === d.image_url) === i)
                    : library.filter((d) => d.category === active);
                const q = presetSearch.trim().toLowerCase();
                const shown = q ? base.filter((d) => (d.name || "").toLowerCase().includes(q)) : base;
                if (tabs.length === 0) return null;
                return (
                  <div className="mt-3" data-testid="preset-picker">
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="preset-tabs">
                      {tabs.map((t) => (
                        <button
                          key={t.key}
                          onClick={() => setPresetTab(t.key)}
                          data-testid={`preset-tab-${t.key}`}
                          className={`px-2.5 py-1 rounded-full font-mono-ui text-[10px] tracking-[0.15em] uppercase border transition ${active === t.key ? "bg-[var(--crimson)] text-white border-[var(--crimson)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)] hover:border-[var(--navy)]"}`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 bg-[var(--bone-2)] rounded-sm px-3 py-2 mb-2">
                      <Search className="w-3.5 h-3.5 text-[var(--ink-soft)]" />
                      <input
                        type="text"
                        value={presetSearch}
                        onChange={(e) => setPresetSearch(e.target.value)}
                        placeholder="Search designs (e.g. coffee, flag)…"
                        className="bg-transparent border-0 outline-none flex-1 text-sm"
                        data-testid="preset-search-input"
                      />
                      {presetSearch && (
                        <button onClick={() => setPresetSearch("")} data-testid="preset-search-clear" aria-label="Clear search" className="text-[var(--ink-soft)] hover:text-[var(--navy)]">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {shown.length > 0 ? (
                      <div className="grid grid-cols-4 gap-2" data-testid="preset-grid">
                        {shown.map((d) => (
                          <button key={`${active}-${d.id}-${d.image_url}`} onClick={() => addPreset(d)} data-testid={`preset-${d.id}`} title={d.name}
                            className="aspect-square rounded-sm overflow-hidden border-2 p-1.5 transition bg-[var(--bone-2)] border-[rgba(27,42,65,0.1)] hover:border-[var(--gold)]">
                            <img src={imageUrl(d.image_url)} alt={d.name} className="w-full h-full object-contain" />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-[var(--ink-soft)] italic py-2" data-testid="preset-empty">
                        {q ? `No designs match “${presetSearch.trim()}”.` : "No designs in this category yet."}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* LAYERS LIST (front → back) */}
              <div className="mt-4 space-y-2" data-testid="layers-list">
                {layers.length === 0 && (
                  <div className="text-xs text-[var(--ink-soft)] italic py-2">No layers yet — add a preset, photo, or text above.</div>
                )}
                {[...layers].reverse().map((layer) => {
                  const sel = layer.id === selectedId;
                  return (
                    <div key={layer.id} onClick={() => setSelectedId(layer.id)} data-testid={`layer-row-${layer.id}`}
                      className={`flex items-center gap-2 p-2 rounded-sm border cursor-pointer transition ${sel ? "border-[var(--gold)] bg-[var(--bone-2)]" : "border-[rgba(27,42,65,0.12)] hover:border-[var(--navy)]/40"}`}>
                      <div className="w-10 h-10 rounded-sm bg-[var(--bone-2)] border border-[rgba(27,42,65,0.1)] grid place-items-center overflow-hidden flex-shrink-0">
                        {layer.type === "text" ? <Type className="w-4 h-4 text-[var(--navy)]" /> : <img src={imageUrl(layer.src)} alt="" className="w-full h-full object-contain" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        {layer.type === "text" ? (
                          <>
                            <input value={layer.text} onChange={(e) => updateLayer(layer.id, { text: e.target.value })} onClick={(e) => e.stopPropagation()} maxLength={40} placeholder="Your text"
                              className="input-field text-sm py-1" data-testid={`layer-text-input-${layer.id}`} />
                            <div className="flex items-center gap-1.5 mt-1.5">
                              {TEXT_COLORS.map((c) => (
                                <button key={c} onClick={(e) => { e.stopPropagation(); updateLayer(layer.id, { color: c }); }}
                                  className={`w-4 h-4 rounded-full border ${layer.color === c ? "border-[var(--gold)] ring-1 ring-[var(--gold)]" : "border-black/20"}`}
                                  style={{ backgroundColor: c }} data-testid={`layer-color-${layer.id}-${c.replace("#", "")}`} />
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-[var(--navy)] truncate">
                            {layer.type === "preset" ? (layer.name || "Preset") : "Uploaded photo"}
                            <span className="ml-1.5 font-mono-ui text-[9px] uppercase tracking-wide text-[var(--ink-soft)]">{layer.type}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button onClick={(e) => { e.stopPropagation(); moveLayer(layer.id, 1); }} title="Bring forward" data-testid={`layer-forward-${layer.id}`} className="p-1 text-[var(--ink-soft)] hover:text-[var(--navy)]"><ChevronUp className="w-4 h-4" /></button>
                        <button onClick={(e) => { e.stopPropagation(); moveLayer(layer.id, -1); }} title="Send back" data-testid={`layer-back-${layer.id}`} className="p-1 text-[var(--ink-soft)] hover:text-[var(--navy)]"><ChevronDown className="w-4 h-4" /></button>
                        <button onClick={(e) => { e.stopPropagation(); deleteLayer(layer.id); }} title="Delete layer" data-testid={`layer-delete-${layer.id}`} className="p-1 text-[var(--crimson)] hover:bg-[var(--crimson)]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {layers.length > 0 && (
                <button onClick={saveDesign} data-testid="save-design-btn"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-sm border border-[var(--navy)] text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--bone)] transition font-mono-ui text-[10px] tracking-[0.2em] uppercase">
                  <Download className="w-3.5 h-3.5" /> Save my design (PNG)
                </button>
              )}
            </div>
          )}

          {/* ITEM NOTE */}
          <div className="pt-4 border-t border-[rgba(27,42,65,0.1)]">
            <label className="block">
              <span className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[var(--navy)] mb-2 block">Notes / Special Requests (optional)</span>
              <textarea data-testid="item-note" value={itemNote} onChange={(e) => setItemNote(e.target.value)} rows={3} maxLength={500}
                placeholder="Anything specific for this item — name spelling, color of lettering, placement, etc." className="input-field resize-y" />
            </label>
          </div>

          {/* QTY + ADD */}
          <div className="pt-4 border-t border-[rgba(27,42,65,0.1)] flex items-center gap-4">
            {!sizedMode && (
              <div className="flex items-center border border-[rgba(27,42,65,0.2)] rounded-sm">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-3 text-[var(--navy)]" data-testid="qty-minus">−</button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
                  className="px-1 font-mono-ui text-sm w-14 text-center bg-transparent border-x border-[rgba(27,42,65,0.2)] py-3 focus:outline-none"
                  data-testid="qty-value"
                />
                <button onClick={() => setQuantity(quantity + 1)} className="px-4 py-3 text-[var(--navy)]" data-testid="qty-plus">+</button>
              </div>
            )}
            <button onClick={handleAddToCart} className="btn-primary btn-crimson flex-1 justify-center" data-testid="add-to-cart-button">
              <ShoppingBag className="w-4 h-4" /> Add to Cart · ${addTotal.toFixed(2)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
